import { useCallback, useEffect, useState } from "react";

import type { FlatStep, StepId } from "../projectTypes";
import { getStarterFiles } from "../utils/project";
import type { FileMap } from "../utils/project";

const SAVE_DELAY_MS = 400;
const EMPTY_CHECKPOINTS: Record<string, unknown> = {};

// What is stored in the browser for one project
interface SavedProgress {
    files?: FileMap;
    code?: string; // The old format: one editor
    stdin?: string;
    currentIndex?: number;
    completedIds?: StepId[];
    checkpoints?: Record<string, unknown>;
}

const readSaved = (key: string): SavedProgress | null => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as SavedProgress) : null;
    } catch {
        return null; // Storage blocked or the data is corrupt
    }
};

// The files to start from: the saved ones (only slots the project has), the old single-editor
// save, or the starter code of the first step
const initialFiles = (saved: SavedProgress | null, firstStep: FlatStep | undefined, slots: string[]): FileMap => {
    const starter = getStarterFiles(firstStep, slots);

    if (saved?.files) {
        const kept = Object.fromEntries(Object.entries(saved.files).filter(([slot]) => slots.includes(slot)));
        return { ...starter, ...kept };
    }
    if (typeof saved?.code === "string") return { ...starter, [slots[0]]: saved.code };

    return starter;
};

// Everything the learner has done in a project, restored from and saved to localStorage.
// It reads the saved copy once, so render the project with a `key` per content id.
export const useProjectProgress = (storageKey: string, steps: FlatStep[], slots: string[]) => {
    const [saved] = useState(() => readSaved(storageKey));

    const [currentIndex, setCurrentIndex] = useState(() =>
        Math.min(saved?.currentIndex ?? 0, Math.max(steps.length - 1, 0))
    );

    // Saved ids of steps that no longer exist are dropped
    const [completedIds, setCompletedIds] = useState<Set<StepId>>(() => {
        const validIds = new Set(steps.map((step) => step.id));
        return new Set((saved?.completedIds ?? []).filter((id) => validIds.has(id)));
    });

    const [files, setFiles] = useState<FileMap>(() => initialFiles(saved, steps[0], slots));
    const [stdin, setStdin] = useState(saved?.stdin ?? "");
    const checkpoints = saved?.checkpoints ?? EMPTY_CHECKPOINTS;

    const setFile = useCallback((slot: string, code: string) => {
        setFiles((current) => ({ ...current, [slot]: code }));
    }, []);

    // A new step brings its own starter code (only for the slots that have some)
    const applyStarter = useCallback(
        (step: FlatStep | undefined) => setFiles((current) => ({ ...current, ...getStarterFiles(step, slots) })),
        [slots]
    );

    // Save a moment after the last change, not on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const progress: SavedProgress = {
                    files,
                    stdin,
                    currentIndex,
                    completedIds: [...completedIds],
                    checkpoints
                };
                localStorage.setItem(storageKey, JSON.stringify(progress));
            } catch (err) {
                console.warn("Could not save progress:", err);
            }
        }, SAVE_DELAY_MS);

        return () => clearTimeout(timer);
    }, [storageKey, files, stdin, currentIndex, completedIds, checkpoints]);

    const markStepDone = useCallback((stepId: StepId) => {
        setCompletedIds((previous) => new Set(previous).add(stepId));
    }, []);

    return {
        currentIndex,
        setCurrentIndex,
        completedIds,
        markStepDone,
        files,
        setFile,
        applyStarter,
        stdin,
        setStdin,
        checkpoints
    };
};